import { useEffect, useMemo, useRef, useState } from 'react';
import { collection, doc, onSnapshot, query, runTransaction, serverTimestamp, where } from 'firebase/firestore';
import { toast } from 'react-toastify';
import { db } from '../../firebase';
import { canPrintDispatchOrder } from '../../lib/dispatchOrders';
import { useSettings } from '../../context/SettingsContext';
import { normalizePrintingSettings } from '../../lib/printing';
import { printKitchen, printTicket } from '../../services/printService';
import {
  isPrintStationEnabled,
  PRINT_STATION_CHANGE_EVENT,
} from '../../lib/printStation';

const LOCAL_PRINTER_SERVER_URL = 'http://localhost:3001';

export default function PrintStationRelay({ currentUser }) {
  const { settings } = useSettings();
  const authorized = canPrintDispatchOrder(currentUser);
  const [enabled, setEnabled] = useState(isPrintStationEnabled);
  const [serverReady, setServerReady] = useState(false);
  const processingRef = useRef(new Set());
  const saleProcessingRef = useRef(new Set());
  const printing = useMemo(
    () => ({
      ...normalizePrintingSettings(settings?.printing),
      printingEnabled: true,
      printingMode: 'server',
      printerServerUrl: LOCAL_PRINTER_SERVER_URL,
      fallbackToBrowserPrint: false,
    }),
    [settings?.printing]
  );

  useEffect(() => {
    const syncEnabled = (event) => {
      setEnabled(event?.detail?.enabled ?? isPrintStationEnabled());
    };
    window.addEventListener(PRINT_STATION_CHANGE_EVENT, syncEnabled);
    window.addEventListener('storage', syncEnabled);
    return () => {
      window.removeEventListener(PRINT_STATION_CHANGE_EVENT, syncEnabled);
      window.removeEventListener('storage', syncEnabled);
    };
  }, []);

  // No reclamar trabajos de Firestore si el printer-server local no esta disponible.
  // Esto evita que un navegador marcado por error como estacion capture un pedido
  // que realmente debe imprimir el PC principal.
  useEffect(() => {
    if (!enabled) {
      setServerReady(false);
      return undefined;
    }

    let cancelled = false;
    const checkLocalServer = async () => {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 2500);
      try {
        const response = await fetch(`${LOCAL_PRINTER_SERVER_URL}/health`, {
          method: 'GET',
          targetAddressSpace: 'loopback',
          cache: 'no-store',
          signal: controller.signal,
        });
        const body = await response.json().catch(() => null);
        if (!cancelled) setServerReady(response.ok && body?.ok === true);
      } catch (_error) {
        if (!cancelled) setServerReady(false);
      } finally {
        clearTimeout(timeoutId);
      }
    };

    checkLocalServer();
    const intervalId = window.setInterval(checkLocalServer, 10000);
    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [enabled]);

  useEffect(() => {
    if (!enabled || !serverReady || !authorized || !currentUser?.uid) return undefined;

    const pendingQuery = query(
      collection(db, 'orders'),
      where('kitchenPrintStatus', 'in', ['pending', 'printing'])
    );

    const unsubscribe = onSnapshot(
      pendingQuery,
      (snapshot) => {
        snapshot.docs.forEach(async (snapshotDoc) => {
          const orderId = snapshotDoc.id;
          if (processingRef.current.has(orderId)) return;
          processingRef.current.add(orderId);

          const orderRef = doc(db, 'orders', orderId);
          let claimedOrder = null;
          let phase = 'reclamar';
          try {
            claimedOrder = await runTransaction(db, async (transaction) => {
              const freshSnapshot = await transaction.get(orderRef);
              if (!freshSnapshot.exists()) return;
              const freshOrder = freshSnapshot.data();
              const claimedAt = freshOrder.kitchenPrintClaimedAt?.toMillis?.() || 0;
              const staleClaim =
                freshOrder.kitchenPrintStatus === 'printing' &&
                claimedAt > 0 &&
                Date.now() - claimedAt > 2 * 60 * 1000;
              if (freshOrder.kitchenPrintStatus !== 'pending' && !staleClaim) return;

              transaction.update(orderRef, {
                kitchenPrintStatus: 'printing',
                kitchenPrintClaimedAt: serverTimestamp(),
                kitchenPrintClaimedBy: currentUser.uid,
              });
              return { id: freshSnapshot.id, ...freshOrder };
            });

            if (!claimedOrder) return;

            phase = 'enviar a la impresora';
            await printKitchen(
              {
                storeName: settings?.ticketDisplayName || settings?.storeName || 'Ole Mita',
                storeLogo: settings?.storeLogo || '',
                table: claimedOrder.table || '',
                waiterName: claimedOrder.waiterName || 'Mesero',
                orderTime: claimedOrder.orderTime || new Date().toISOString(),
                items: (claimedOrder.items || []).map((item) => ({
                  name: item.name || 'Producto',
                  quantity: Number(item.quantity || 0),
                  observation: item.observation || '',
                })),
                notes: claimedOrder.notes || '',
                footer: 'A despacho',
              },
              printing
            );

            phase = 'confirmar impresion';
            await runTransaction(db, async (transaction) => {
              const freshSnapshot = await transaction.get(orderRef);
              if (!freshSnapshot.exists()) return;
              if (freshSnapshot.data().kitchenPrintClaimedBy !== currentUser.uid || freshSnapshot.data().kitchenPrintStatus !== 'printing') return;
              transaction.update(orderRef, {
                kitchenPrintStatus: 'printed',
                kitchenPrintedAt: serverTimestamp(),
                kitchenPrintError: null,
              });
            });
          } catch (error) {
            console.error('La estacion local no pudo imprimir el pedido:', error);
            if (claimedOrder) {
              try {
                await runTransaction(db, async (transaction) => {
                  const freshSnapshot = await transaction.get(orderRef);
                  if (!freshSnapshot.exists()) return;
                  if (freshSnapshot.data().kitchenPrintClaimedBy !== currentUser.uid || freshSnapshot.data().kitchenPrintStatus !== 'printing') return;
                  transaction.update(orderRef, {
                    kitchenPrintStatus: 'error',
                    kitchenPrintFailedAt: serverTimestamp(),
                    kitchenPrintError: String(error?.message || error).slice(0, 500),
                  });
                });
              } catch (updateError) {
                console.error('No se pudo registrar el error de impresion:', updateError);
              }
            }
            toast.error(`Pedido ${claimedOrder?.table || snapshotDoc.data().table || orderId}: fallo al ${phase}${error?.code === 'permission-denied' ? ' (permiso de Firestore)' : ''}.`);
          } finally {
            processingRef.current.delete(orderId);
          }
        });
      },
      (error) => console.error('No se pudo escuchar la cola de impresion:', error)
    );

    return () => unsubscribe();
  }, [currentUser?.uid, authorized, enabled, serverReady, printing, settings?.storeName, settings?.ticketDisplayName, settings?.storeLogo]);


  useEffect(() => {
    if (!enabled || !serverReady || !authorized || !currentUser?.uid) return undefined;

    const pendingSalesQuery = query(
      collection(db, 'orders'),
      where('salePrintStatus', 'in', ['pending', 'printing'])
    );

    const unsubscribe = onSnapshot(
      pendingSalesQuery,
      (snapshot) => {
        snapshot.docs.forEach(async (snapshotDoc) => {
          const orderId = snapshotDoc.id;
          if (saleProcessingRef.current.has(orderId)) return;
          saleProcessingRef.current.add(orderId);

          const orderRef = doc(db, 'orders', orderId);
          let claimedOrder = null;
          let phase = 'reclamar';
          try {
            claimedOrder = await runTransaction(db, async (transaction) => {
              const freshSnapshot = await transaction.get(orderRef);
              if (!freshSnapshot.exists()) return;
              const freshOrder = freshSnapshot.data();
              const claimedAt = freshOrder.salePrintClaimedAt?.toMillis?.() || 0;
              const staleClaim =
                freshOrder.salePrintStatus === 'printing' &&
                claimedAt > 0 &&
                Date.now() - claimedAt > 2 * 60 * 1000;
              if (freshOrder.salePrintStatus !== 'pending' && !staleClaim) return;

              transaction.update(orderRef, {
                salePrintStatus: 'printing',
                salePrintClaimedAt: serverTimestamp(),
                salePrintClaimedBy: currentUser.uid,
              });
              return { id: freshSnapshot.id, ...freshOrder };
            });

            if (!claimedOrder) return;

            const items = Array.isArray(claimedOrder.items) ? claimedOrder.items : [];
            const subtotal = Number(
              claimedOrder.subtotal ??
                items.reduce(
                  (sum, item) => sum + Number(item.price || 0) * Number(item.quantity || 0),
                  0
                )
            );
            const total = Number(claimedOrder.saleTotal ?? claimedOrder.total ?? subtotal);
            const paidAt = claimedOrder.paidAt?.toDate?.();

            phase = 'enviar a la impresora';
            await printTicket(
              {
                storeName: settings?.ticketDisplayName || settings?.storeName || 'Ole Mita',
                storeLogo: settings?.storeLogo || '',
                address: settings?.address || '',
                phone: settings?.phone || '',
                email: settings?.email || '',
                invoiceNumber: claimedOrder.saleInvoice || '',
                date: paidAt?.toISOString?.() || claimedOrder.orderTime || new Date().toISOString(),
                customerName: claimedOrder.customerName || 'Consumidor Final',
                table: claimedOrder.table || '',
                waiterName: claimedOrder.waiterName || '',
                items: items.map((item) => ({
                  name: item.name || 'Producto',
                  quantity: Number(item.quantity || 0),
                  price: Number(item.price || 0),
                  total: Number(item.price || 0) * Number(item.quantity || 0),
                })),
                subtotal,
                taxEnabled: Boolean(settings?.taxEnabled),
                tax: Number(claimedOrder.taxes || 0),
                taxLabel: settings?.taxEnabled ? `Impuesto (${settings?.taxRate || 0}%)` : 'Impuesto',
                total,
                paymentMethod: claimedOrder.paymentMethod || '',
                amountReceived: Number(claimedOrder.amountReceived || 0),
                secondPaymentMethod: claimedOrder.secondPaymentMethod || '',
                secondAmountReceived: Number(claimedOrder.secondAmountReceived || 0),
                change: Number(claimedOrder.change || 0),
                footer: 'Gracias',
              },
              printing
            );

            phase = 'confirmar impresion';
            await runTransaction(db, async (transaction) => {
              const freshSnapshot = await transaction.get(orderRef);
              if (!freshSnapshot.exists()) return;
              if (freshSnapshot.data().salePrintClaimedBy !== currentUser.uid || freshSnapshot.data().salePrintStatus !== 'printing') return;
              transaction.update(orderRef, {
                salePrintStatus: 'printed',
                salePrintedAt: serverTimestamp(),
                salePrintError: null,
              });
            });
          } catch (error) {
            console.error('La estacion local no pudo imprimir el ticket de venta:', error);
            if (claimedOrder) {
              try {
                await runTransaction(db, async (transaction) => {
                  const freshSnapshot = await transaction.get(orderRef);
                  if (!freshSnapshot.exists()) return;
                  if (freshSnapshot.data().salePrintClaimedBy !== currentUser.uid || freshSnapshot.data().salePrintStatus !== 'printing') return;
                  transaction.update(orderRef, {
                    salePrintStatus: 'error',
                    salePrintFailedAt: serverTimestamp(),
                    salePrintError: String(error?.message || error).slice(0, 500),
                  });
                });
              } catch (updateError) {
                console.error('No se pudo registrar el error de impresion de venta:', updateError);
              }
            }
            toast.error(`Venta ${claimedOrder?.saleInvoice || claimedOrder?.table || orderId}: fallo al ${phase}${error?.code === 'permission-denied' ? ' (permiso de Firestore)' : ''}.`);
          } finally {
            saleProcessingRef.current.delete(orderId);
          }
        });
      },
      (error) => console.error('No se pudo escuchar la cola de impresion de ventas:', error)
    );

    return () => unsubscribe();
  }, [currentUser?.uid, authorized, enabled, serverReady, printing, settings]);

  return null;
}
